import { Button } from '@heidi/design-system/button';
import { Link } from '@heidi/design-system/link';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ExternalLink, ImageOff } from 'lucide-react';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { VisualDxDiagnosis } from '../../contracts';

const messages = defineMessages({
  imageAlt: {
    id: 'jDEOrFNiG3',
    defaultMessage: 'VisualDx clinical image for {name}',
  },
  viewDiagnosis: {
    id: 'iqpAzTN1nV',
    defaultMessage: 'View on VisualDx',
  },
  selectDiagnosis: {
    id: 'M5EXFaUb9M',
    defaultMessage: 'Select a diagnosis',
  },
  compareAll: {
    id: 'iV2sve/a87',
    defaultMessage: 'Compare all on VisualDx',
  },
});

/**
 * A card item is a BE ``VisualDxDiagnosis`` plus an optional clamped
 * description line, used by the side-by-side comparator for the synopsis.
 */
export type VisualDxDiagnosisCardItem = VisualDxDiagnosis & {
  description?: string | null;
};

interface VisualDxDiagnosisCardsProps {
  diagnoses: VisualDxDiagnosisCardItem[];
  compare_url?: string | null;
  /** Called when the user clicks a diagnosis card. */
  onSelect?: (diagnosisName: string) => void;
  className?: string;
}

interface DiagnosisCardProps {
  diagnosis: VisualDxDiagnosisCardItem;
  onSelect?: (name: string) => void;
}

const DiagnosisCard = ({ diagnosis, onSelect }: DiagnosisCardProps) => {
  const intl = useIntl();
  const [imageError, setImageError] = useState(false);

  const handleClick = () => {
    onSelect?.(diagnosis.name);
  };

  const cardBody = (
    <>
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-background-secondary">
        {diagnosis.thumbnail_url && !imageError ? (
          // VisualDx CDN is per-token dynamic URLs; same pattern as
          // EvidenceImageBlock (next/image domain whitelist impractical).
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={diagnosis.thumbnail_url}
            alt={intl.formatMessage(messages.imageAlt, { name: diagnosis.name })}
            className="size-full object-cover transition-transform duration-200 group-hover:scale-105"
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="flex size-full items-center justify-center">
            <ImageOff className="size-8 text-text-tertiary" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1 p-2.5">
        <TypographyP2Bold className="line-clamp-2 text-text-primary group-hover:text-text-link">
          {diagnosis.name}
        </TypographyP2Bold>
        {diagnosis.description ? (
          <TypographyCaption
            className="line-clamp-4 text-text-secondary"
            title={diagnosis.description}
          >
            {diagnosis.description}
          </TypographyCaption>
        ) : null}
      </div>
    </>
  );

  // With a selection callback, the whole card is one button so assistive
  // technology announces one action. Without one (the Evidence-child surface),
  // render honest read-only content and leave only the working VisualDx link
  // focusable.
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-surface transition-colors duration-150 hover:border-border-hover hover:bg-surface-hover">
      {onSelect ? (
        <Button
          size="lg"
          variant="ghost"
          flush="all"
          disableAutomaticTypographyClasses
          onClick={handleClick}
          aria-label={diagnosis.name}
          className="group flex h-auto w-full flex-col items-stretch gap-0 rounded-none p-0 text-left"
        >
          {cardBody}
        </Button>
      ) : (
        <div className="group flex w-full flex-col items-stretch gap-0">{cardBody}</div>
      )}

      {diagnosis.visualdx_url && (
        <Link
          href={diagnosis.visualdx_url}
          target="_blank"
          rel="noopener noreferrer"
          variant="inline"
          className="inline-flex items-center gap-1 px-2.5 pb-2.5"
        >
          <TypographyCaption className="text-text-link hover:underline">
            {intl.formatMessage(messages.viewDiagnosis)}
          </TypographyCaption>
          <ExternalLink className="size-3 shrink-0 text-text-link" />
        </Link>
      )}
    </div>
  );
};

/**
 * Interactive grid of VisualDx diagnosis cards.
 *
 * Rendered below the status header in ``VisualDxBlock`` when a completed
 * ``enrich_diagnoses_with_links`` chunk includes structured diagnosis data.
 * Cards are individually clickable when ``onSelect`` is provided. Otherwise
 * their content is read-only and only the VisualDx detail link is interactive.
 */
export const VisualDxDiagnosisCards = ({
  diagnoses,
  compare_url,
  onSelect,
  className,
}: VisualDxDiagnosisCardsProps): JSX.Element | null => {
  const intl = useIntl();

  if (diagnoses.length === 0) return null;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {onSelect ? (
        <TypographyCaption className="text-text-secondary">
          {intl.formatMessage(messages.selectDiagnosis)}
        </TypographyCaption>
      ) : null}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {diagnoses.slice(0, 8).map((diagnosis, index) => (
          <DiagnosisCard
            key={`${diagnosis.name}-${index}`}
            diagnosis={diagnosis}
            onSelect={onSelect}
          />
        ))}
      </div>

      {compare_url && (
        <Link
          href={compare_url}
          target="_blank"
          rel="noopener noreferrer"
          variant="inline"
          className="inline-flex items-center gap-1.5 self-start"
        >
          <TypographyCaption className="text-text-link hover:underline">
            {intl.formatMessage(messages.compareAll)}
          </TypographyCaption>
          <ExternalLink className="size-3 shrink-0 text-text-link" />
        </Link>
      )}
    </div>
  );
};
